import React, { useState, useEffect } from 'react';
import { Key, UserCheck, AlertCircle, Info } from 'lucide-react';
import * as api from '../../services/api';

function CustomerSettings() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [xtreamUser, setXtreamUser] = useState('');
  const [xtreamPass, setXtreamPass] = useState('');

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await api.customerGetProfile();
      setProfile(res.data);
      setXtreamUser(res.data.xtream?.xtream_username || '');
    } catch (err) {
      setError('Failed to load profile');
    } finally { setLoading(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    try {
      await api.customerChangePassword(currentPw, newPw);
      setMessage('Password updated successfully');
      setCurrentPw(''); setNewPw('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  const updateXtream = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    try {
      await api.customerUpdateXtream(xtreamUser, xtreamPass);
      setMessage('Xtream credentials updated');
      setXtreamPass('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update Xtream credentials');
    }
  };

  if (loading) return <div className="page-header"><h2>Loading...</h2></div>;

  return (
    <div>
      <div className="page-header">
        <h2>Settings</h2>
        <p style={{ color: '#6b7280' }}>Manage your account and connection details</p>
      </div>

      {message && <div style={{ padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', marginBottom: '15px' }}>{message}</div>}
      {error && <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', marginBottom: '15px' }}><AlertCircle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />{error}</div>}

      <div className="table-container" style={{ marginBottom: '20px', background: '#f0f7ff', border: '1px solid #93c5fd' }}>
        <div className="table-header" style={{ cursor: 'pointer' }} onClick={() => setShowInfo(!showInfo)}>
          <h3><Info size={18} style={{ marginRight: '8px' }} />What are these settings? {showInfo ? '▲' : '▼'}</h3>
        </div>
        {showInfo && (
          <div style={{ padding: '20px', lineHeight: '1.7' }}>
            <p style={{ marginBottom: '12px' }}><strong>You have two separate logins. They serve different purposes:</strong></p>
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontWeight: '600', color: '#1e40af' }}>🔑 Portal Password</p>
              <p style={{ color: '#4b5563', fontSize: '14px' }}>
                This is the password you use to <strong>log into this website</strong> (the customer portal).
                Use it to browse your channels, manage content, and create custom categories.
                Changing this does NOT affect your TV apps.
              </p>
            </div>
            <div>
              <p style={{ fontWeight: '600', color: '#1e40af' }}>📺 Xtream Codes Credentials</p>
              <p style={{ color: '#4b5563', fontSize: '14px' }}>
                These are the credentials your <strong>IPTV apps</strong> use to connect and stream content.
                When you set up TiviMate, IPTV Smarters, VLC, Chillio, or any other IPTV app,
                you'll enter this username and password along with the server URL from your dashboard.
                Changing these will require you to update the credentials in all your TV apps.
              </p>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
        <div className="table-container">
          <div className="table-header"><h3><Key size={18} style={{ marginRight: '8px' }} />Change Portal Password</h3></div>
          <form onSubmit={changePassword} style={{ padding: '20px' }}>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}>
              This changes the password you use to log into this web portal. Your TV apps will not be affected.
            </p>
            <div className="form-group"><label>Current Password</label><input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required /></div>
            <div className="form-group"><label>New Password</label><input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required /></div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Update Password</button>
          </form>
        </div>

        <div className="table-container">
          <div className="table-header"><h3><UserCheck size={18} style={{ marginRight: '8px' }} />Xtream Codes Credentials</h3></div>
          <form onSubmit={updateXtream} style={{ padding: '20px' }}>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '15px' }}>
              These are the credentials your IPTV apps (TiviMate, Smarters, VLC, Chillio, etc.) use to connect to the service. The Server URL can be found on your dashboard.
            </p>
            <div className="form-group"><label>Xtream Username</label><input type="text" value={xtreamUser} onChange={e => setXtreamUser(e.target.value)} required /></div>
            <div className="form-group"><label>New Xtream Password</label><input type="password" value={xtreamPass} onChange={e => setXtreamPass(e.target.value)} placeholder="Leave blank to keep current" /></div>
            <button type="submit" className="btn btn-success" style={{ width: '100%' }}>Update Xtream Credentials</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CustomerSettings;
